import { type ModalProps, useModal } from '@omnipresentgroup/design-system';
import { useMemo, useState } from 'react';

export type Step<T> = {
  title: string;
  property: keyof T;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: (props: StepComponentProps<T> & any) => JSX.Element;
};

type StepNavigationProps<T> = {
  handleClick?: (updatedData: Partial<T>, step?: number) => void;
  disabled?: boolean;
  loading?: boolean;
};

export type StepComponentProps<T> = {
  data: Partial<T> | undefined;
  next: StepNavigationProps<T>;
  back: StepNavigationProps<T>;
};

export type StepSummaryModalProps<T> = {
  modalProps: Omit<ModalProps, 'children'>;
  handleCancel: () => void;
  handleSubmit: () => void;
  loading?: boolean;
  values: T;
};

type Props<T, P> = {
  steps: Step<T>[];
  modal: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: (props: StepSummaryModalProps<T> & any) => JSX.Element;
    handleSubmit: (data: T) => void;
  };
  back?: StepNavigationProps<T>;
  next?: StepNavigationProps<T>;
} & P;

export function Stepper<T, P>({
  steps,
  back,
  next,
  modal,
  ...others
}: Props<T, P>) {
  const [activeStep, setActiveStep] = useState<number>(0);

  const [data, setData] = useState<Partial<T>>();

  const { modalProps } = useModal({});
  const [modalOpen, setModalOpen] = useState(false);

  const handleBack = (updatedData: Partial<T>) => {
    setData((previousData) =>
      previousData ? { ...previousData, ...updatedData } : { ...updatedData },
    );

    activeStep > 0 && setActiveStep((previousStep) => previousStep - 1);
    back?.handleClick?.(updatedData, activeStep);
  };

  const handleNext = (updatedData: Partial<T>) => {
    setData((previousData) =>
      previousData ? { ...previousData, ...updatedData } : { ...updatedData },
    );

    if (activeStep === steps.length - 1) {
      setModalOpen(true);
    }
    if (activeStep < steps.length - 1) {
      setActiveStep((previousStep) => previousStep + 1);
    }

    next?.handleClick?.(updatedData, activeStep);
  };

  const isDataReady = useMemo(
    () => steps.every((step) => !!data?.[step.property]),
    [data, steps],
  );

  const showModal = useMemo(
    () => isDataReady && activeStep === steps.length - 1,
    [isDataReady, activeStep, steps.length],
  );

  return (
    <>
      {steps.map(
        (step, index) =>
          index === activeStep && (
            <step.component
              key={index}
              data={data}
              back={{
                handleClick: (updatedData: Partial<T>) =>
                  handleBack(updatedData),
                disabled: back?.disabled,
                loading: back?.loading,
              }}
              next={{
                handleClick: (updatedData: Partial<T>) =>
                  handleNext(updatedData),
                disabled: next?.disabled,
                loading: next?.loading,
              }}
              {...others}
            />
          ),
      )}
      {showModal && (
        <modal.component
          modalProps={{
            ...modalProps,
            open: modalOpen,
            onOpenChange: setModalOpen,
            defaultOpen: true,
          }}
          handleCancel={() => {
            setModalOpen(false);
          }}
          handleSubmit={() => isDataReady && modal.handleSubmit(data as T)}
          values={data as T}
          {...others}
        />
      )}
    </>
  );
}
